import React from 'react';
import { Card, CardContent, Typography, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PaletteIcon from '@mui/icons-material/Palette';
import { getMeasureIcon, getMeasureUnit, getMeasureLabel } from '../utils/measurementConfig';

const tileSizeConfig = {
    compact: {
        height: 150,
        width: 210,
        iconSize: 20,
        titleVariant: 'subtitle1',
        valueVariant: 'body2',
        showLabels: false,
        padding: 1.5,
        rowSpacing: 1.5,
    },
    normal: {
        height: 190,
        iconSize: 28,
        titleVariant: 'h6',
        valueVariant: 'subtitle2',
        showLabels: true,
        padding: 2,
        rowSpacing: 2,
    },
    expanded: {
        height: 280,
        width: 380,
        iconSize: 36,
        titleVariant: 'h5',
        valueVariant: 'h6',
        showLabels: true,
        padding: 3,
        rowSpacing: 2.5,
    },
};

function MeasureItem({ type, value, config, t, fullWidth = false }) {
    if (value == null) {
        return null;
    }

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: fullWidth ? 'none' : 1,
            minWidth: 0
        }}>
            {getMeasureIcon(type, { fontSize: config.iconSize })}
            <Box>
                {config.showLabels && (
                    <Typography variant="caption" display="block" color="text.secondary">
                        {getMeasureLabel(type, t)}
                    </Typography>
                )}
                <Typography variant={config.valueVariant}>
                    {value} {getMeasureUnit(type)}
                </Typography>
            </Box>
        </Box>
    );
}

function SensorTile({ zoneData, onClick, tileSize = 'normal', backgroundColor = '#ffffff', onColorPickerOpen, isEditMode = false }) {
    const { t } = useTranslation();
    const config = tileSizeConfig[tileSize] || tileSizeConfig.normal;

    const latestFeed = zoneData.feeds?.[0];
    const latestMeasures = latestFeed?.measures || [];
    const lastUpdate = latestFeed?.creationDate
        ? dayjs(latestFeed.creationDate).format('YYYY/MM/DD HH:mm:ss')
        : t('common.noData');

    const measuresMap = latestMeasures.reduce((acc, measure) => {
        acc[measure.type] = measure.value;
        return acc;
    }, {});

    if (zoneData.zone === null && latestMeasures.length === 0) {
        return null;
    }

    const secondRowMeasure = measuresMap.POWE != null ? 'POWE' :
                             measuresMap.PRES != null ? 'PRES' : null;

    const handleColorClick = (event) => {
        event.stopPropagation();
        if (onColorPickerOpen) {
            onColorPickerOpen(zoneData.zone);
        }
    };

    return (
        <Card
            sx={{
                width: config.width || '100%',
                height: config.height,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                backgroundColor: backgroundColor,
                '&:hover': {
                    boxShadow: 6,
                    '& .color-picker-button': {
                        opacity: 1,
                    }
                },
                transition: 'box-shadow 0.2s ease-in-out',
                position: 'relative',
            }}
            onClick={() => onClick(zoneData.zone)}
        >
            {/* Color Picker Button - Only show when not in edit mode */}
            {!isEditMode && (
                <IconButton
                    className="color-picker-button"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out',
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        },
                    }}
                    size="small"
                    onClick={handleColorClick}
                >
                    <PaletteIcon fontSize="small" />
                </IconButton>
            )}

            <CardContent sx={{ flexGrow: 1, p: config.padding, display: 'flex', flexDirection: 'column' }}>
                {/* Zone Name */}
                <Typography variant={config.titleVariant} component="div" noWrap>
                    {zoneData.zone || t('common.unassignedZone')}
                </Typography>

                {/* Last Update */}
                <Typography color="text.secondary" variant="caption" sx={{ mb: config.rowSpacing }}>
                    {t('common.lastUpdate')}: {tileSize === 'compact' ? dayjs(latestFeed?.creationDate).format('HH:mm') : lastUpdate}
                </Typography>

                {/* Row 1: Temperature + Humidity */}
                <Box sx={{ display: 'flex', gap: 2, mb: config.rowSpacing }}>
                    <MeasureItem type="TEMP" value={measuresMap.TEMP} config={config} t={t} />
                    <MeasureItem type="HUMI" value={measuresMap.HUMI} config={config} t={t} />
                </Box>

                {/* Row 2: Power OR Atmospheric Pressure (full width) */}
                {secondRowMeasure && (
                    <Box sx={{ display: 'flex' }}>
                        <MeasureItem
                            type={secondRowMeasure}
                            value={measuresMap[secondRowMeasure]}
                            config={config}
                            t={t}
                            fullWidth
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default SensorTile;
